import { FC } from 'react'
import { LatestContractDtoFragment, PublicProfileFragment, RoleName } from '../../../types'
import { EmployeeInformationComponent } from '../personal/EmployeeInformationComponent'
import { EducationTable } from '../personal/EducationTable'
import { ContractComponent } from '../contract/ContractComponent'
import { Divider, Col } from 'antd'
import { SalaryHistoryTable } from '../contract/SalaryHistoryTable'
import { BonusHistoryTable } from '../contract/BonusHistoryTable'
import { NationalContractLevelHistoryTable } from '../contract/NationalContractLevelHistoryTable'
import { LevelHistoryTable } from '../contract/LevelHistoryTable'
import { PaymentHistoryTable } from '../contract/PaymentHistoryTable'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import useResignedEmployeeOverviewTabData from '../../../graphql/contract/hooks/useResignedEmployeeOverviewTabData'
import { useAuth } from '../../../context/auth-context'
import { GovernmentJobTitleHistoryTable } from '../contract/GovernmentJobTitleHistoryTable'
import { KudosCarousel } from '../../../components/custom_components/Carousel/KudosCarousel'
import {
  useAttentionPointsForResignedEmployeeWithCache,
  useKudosForResignedEmployeeWithCache,
} from '../../../graphql/kudos/useKudosForEmployeeWithCache'
import useIsFeatureEnabled from '../../../graphql/featureToggle/useIsFeatureEnabled'
import { FeatureNameEnum } from '../../../graphql/featureToggle/FeatureNameEnum'
import PerText from '../../../components/custom_components/Text'
import { useTranslation } from 'react-i18next'
import AttentionPointCarousel from '../../../components/custom_components/Carousel/AttentionPointCarousel'
import { NewLoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'

interface ResignedEmployeeOverviewTabProps {
  profile: PublicProfileFragment
}

export const ResignedEmployeeOverviewTab: FC<ResignedEmployeeOverviewTabProps> = ({ profile }) => {
  const { user } = useAuth()
  const { t } = useTranslation()
  const { isFeatureEnabled: isKudosEnvVarEnabled } = useIsFeatureEnabled(FeatureNameEnum.KUDOS)
  const timezone = {
    locale: user.locale,
    timezone: profile.employeeTimezone,
  }
  const isUserHr = !!user.roles && user.roles.includes(RoleName.Hr)

  const {
    isKudosEnabled,
    employeeOptedOut,
    currentKudos,
    currentPage,
    totalPages,
    loading: kudosLoading,
    error: kudosError,
    goToNextPage,
    goToPreviousPage,
    hasNextPage,
    hasPreviousPage,
  } = useKudosForResignedEmployeeWithCache(profile.employeeId)

  const {
    currentAttentionPoints,
    currentPage: currentPageAP,
    totalPages: totalPagesAP,
    loading: loadingAP,
    error: errorAP,
    goToNextPage: goToNextPageAP,
    goToPreviousPage: goToPreviousPageAP,
    hasNextPage: hasNextPageAP,
    hasPreviousPage: hasPreviousPageAP,
  } = useAttentionPointsForResignedEmployeeWithCache(profile.employeeId, isUserHr)

  const { data, error, loading } = useResignedEmployeeOverviewTabData(profile.employeeId, profile.country)

  if (loading || error || !data || !data.resignedContract || !data.tagHistoryOfResignedEmployeeUntilReplyEntryDate || !data.careerPath) {
    return (
      <NewLoadingAndErrors
        isLoading={loading}
        error={error}
        hasNoData={!data || (!data.resignedContract && !data.tagHistoryOfResignedEmployeeUntilReplyEntryDate && !data.careerPath)}
        showSpinner
        t={t}
      />
    )
  }

  const resignedContract: LatestContractDtoFragment = data.resignedContract

  return (
    <>
      <PerFlexRow>
        <Col span={12}>
          <EmployeeInformationComponent
            profile={profile}
            currentContract={resignedContract}
            publicProfile={true}
            tagHistory={data.tagHistoryOfResignedEmployeeUntilReplyEntryDate}
            timezone={timezone}
          />
        </Col>
        <Col span={12}>
          <ContractComponent
            currentContract={resignedContract}
            businessUnit={profile.businessUnit ? profile.businessUnit : undefined}
            careerPathEvaluation={data.resignedEmployeeCareerPathEvaluation}
            careerPath={data.careerPath}
            isPublic
            isResigned
            timezone={timezone}
            branchOfficeCode={profile.branchOfficeCode}
          />
        </Col>
      </PerFlexRow>

      {isKudosEnvVarEnabled && (
        <>
          {isKudosEnabled && (
            <>
              <Divider />
              <PerText appearance="tile-heading" mb="s">
                {t('kudos.header')}
              </PerText>
              <KudosCarousel
                privateProfileView={false}
                employeeOptedOut={employeeOptedOut}
                kudos={currentKudos}
                nameDisplayPreference={user.nameDisplayPreference}
                loading={kudosLoading}
                error={kudosError || null}
                currentPage={currentPage}
                totalPages={totalPages}
                onNextPage={goToNextPage}
                onPreviousPage={goToPreviousPage}
                hasNextPage={hasNextPage}
                hasPreviousPage={hasPreviousPage}
              />
            </>
          )}
          {isUserHr && currentAttentionPoints.length > 0 && (
            <>
              <Divider />
              <PerText appearance="tile-heading" mb="s">
                {t('attentionPoint.header')}
              </PerText>
              <AttentionPointCarousel
                attentionPoints={currentAttentionPoints}
                nameDisplayPreference={user.nameDisplayPreference}
                loading={loadingAP}
                error={errorAP || null}
                currentPage={currentPageAP}
                totalPages={totalPagesAP}
                onNextPage={goToNextPageAP}
                onPreviousPage={goToPreviousPageAP}
                hasNextPage={hasNextPageAP}
                hasPreviousPage={hasPreviousPageAP}
              />
            </>
          )}
        </>
      )}

      <SalaryHistoryTable
        employeeId={profile.employeeId}
        isMonthlySalaryDisplayed={resignedContract.isMonthlySalaryDisplayed}
        monthlySalaryMonthsInYear={resignedContract.monthlySalaryMonthsInYear}
        timezone={timezone}
        resigned={true}
      />

      <BonusHistoryTable employeeId={profile.employeeId} timezone={timezone} resigned={true} />

      <GovernmentJobTitleHistoryTable employeeId={profile.employeeId} timezone={timezone} resigned={true} />

      <NationalContractLevelHistoryTable employeeId={profile.employeeId} timezone={timezone} resigned={true} />

      <LevelHistoryTable employeeId={profile.employeeId} timezone={timezone} resigned={true} />

      <PaymentHistoryTable employeeId={profile.employeeId} timezone={timezone} resigned={true} />

      {profile.education && profile.education.length > 0 && (
        <>
          <Divider />
          <EducationTable education={profile.education} timezone={timezone} />
        </>
      )}
    </>
  )
}
